import { inject, Injectable } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { GlobalErrorService } from '@services/global-error.service'
import { MemberService } from '@features/insured/services/member/member.service'
import { MemberForm } from '@features/insured/models/member/member.model'
import { memberExistsValidator } from '@features/insured/validators/member/member-exists.validator'
import { CertificateCategory } from '@shared/models/certificate-category.model'
import { buildAlbertValueObjectGroup, requiredWhenChecked, requiredWithoutExternalValue } from '@core/utils/form-utils'

@Injectable({
  providedIn: 'root'
})
export class MemberFormService {
  private readonly fb = inject(FormBuilder)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly apiService = inject(MemberService)

  public buildForm(): FormGroup<MemberForm> {
    return this.fb.group({
      uuid: this.fb.control<string | null>(null),
      externalId: this.fb.control<string | null>(null),
      companyCode: buildAlbertValueObjectGroup<string>(this.fb, null, [requiredWithoutExternalValue]),
      name: buildAlbertValueObjectGroup<string>(this.fb, null, [requiredWithoutExternalValue]),
      street1: buildAlbertValueObjectGroup<string>(this.fb, ''),
      street2: buildAlbertValueObjectGroup<string>(this.fb, ''),
      street3: buildAlbertValueObjectGroup<string>(this.fb, ''),
      postalCode: buildAlbertValueObjectGroup<string>(this.fb, ''),
      city: buildAlbertValueObjectGroup<string>(this.fb, ''),
      state: buildAlbertValueObjectGroup<string>(this.fb, ''),
      country: buildAlbertValueObjectGroup<string>(this.fb, ''),
      additionalAddressInformation: buildAlbertValueObjectGroup<string>(this.fb, '', [], true),
      parentCompany: buildAlbertValueObjectGroup<string>(this.fb, '', [requiredWhenChecked], true),
      category: this.fb.control<CertificateCategory | null>(null),
      comment: this.fb.control<string | null>(null),
    }, { updateOn: 'blur' }) as FormGroup<MemberForm>
  }

  public applyCreateDefaults(form: FormGroup<MemberForm>, category: CertificateCategory): void {
    form.reset()
    form.patchValue({ uuid: null })
    this.addAsyncValidators(form, category)
    form.controls.name.controls.localValue.markAsTouched()
  }

  public addAsyncValidators(form: FormGroup<MemberForm>, category: CertificateCategory, initialName?: string | null): void {
    const control = form.controls.name.controls.localValue
    control.setAsyncValidators([memberExistsValidator(this.apiService, this.globalErrorService, category, initialName)])
    control.updateValueAndValidity({ emitEvent: false })
  }
}
